import { z } from 'zod';
import { urlSchema } from '../../../schemas/common/url.js';
import { addressSchema } from '../../Address.js';
import { checkoutDisclosuresSchema } from '../../CheckoutDisclosures.js';
import { discountCampaignSchema } from '../../DiscountCampaign.js';
import { paymentConfigSchema } from '../../PaymentConfig.js';
import { preorderPaymentConfigSchema } from '../../preorders/PreorderPaymentConfig.js';
import { reEngagementConfigSchema } from '../../ReEngagementConfig.js';
import { StateAbbreviationSchema, StateNameSchema } from '../../StateAbbreviation.js';
import { productConfigurationSchema } from '../constants/ProductConfigurationOptions.js';
import { ServicesSchema, Services } from '../services/Services.js';
import { serviceConfigSchema } from './SharedServiceConfig.js';

// ============================================================================
// Enums
// ============================================================================
const SaleAssignmentMethod = {
    CLOSEST_DEALER: 'CLOSEST_DEALER', // partner with this configuration favors proximity over PMAs
    PRIMARY_MARKET_AREA: 'PRIMARY_MARKET_AREA', // partner with this configuration respects PMAs
    FIXED: 'FIXED', // partner with this configuration has only one seller (e.g. Ekho Access, LiveWire)
    BUYER_PREFERENCE: 'BUYER_PREFERENCE', // partner with this configuration defers to buyer preference
};
const saleAssignmentMethodValues = [
    SaleAssignmentMethod.CLOSEST_DEALER,
    SaleAssignmentMethod.PRIMARY_MARKET_AREA,
    SaleAssignmentMethod.FIXED,
    SaleAssignmentMethod.BUYER_PREFERENCE,
];
const SaleAssignmentMethodSchema = z.enum(saleAssignmentMethodValues);
// ============================================================================
// Nested Schemas
// ============================================================================
const saleAssignmentConfigurationLimitedStateSchema = z.object({
    enabled: z.boolean(),
    excludedZipCodes: z.array(z.string()).optional(),
    excludedCities: z.array(z.string()).optional(),
});
const saleAssignmentConfigurationSchema = z.object({
    primaryMethod: SaleAssignmentMethodSchema,
    fallbackMethod: SaleAssignmentMethodSchema,
    // only required if states need to be limited for sale assignment
    limitedStates: z
        .record(StateAbbreviationSchema, saleAssignmentConfigurationLimitedStateSchema)
        .optional(),
    fixedSellingServicesProviderId: z.string().optional(), // if primaryMethod is FIXED, this is the fixed selling services provider id
});
const statesSupportedMapSchema = z.record(StateAbbreviationSchema, z.object({
    stateFullName: StateNameSchema,
}));
// ============================================================================
// Website Schema
// ============================================================================
const programOwnerWebsiteSchema = z.object({
    domains: z.record(urlSchema, z.string()), // key is the url path, value is the domain owner svp id
});
// ============================================================================
// Configurator Schema
// ============================================================================
const programOwnerConfiguratorSchema = z.object({
    // Controls whether the model year selector is enabled
    isModelYearSelectionEnabled: z.boolean().optional(),
    // Controls whether the vehicle selector is enabled
    isVehicleSelectorEnabled: z.boolean().optional(),
    // This will inform which vehicles are available in the vehicle selector and model year selector
    configuratorVehicles: z.record(z.string(), z.array(z.string())).optional(), // key is the oemId, value is an array of canonVehicleIds
});
// ============================================================================
// Preorder Schema
// ============================================================================
const programOwnerPreorderSchema = z.object({
    // Required to enable creation of preorder checkout carts
    enabled: z.boolean(),
    // required if enabled is true
    preorderPaymentConfig: preorderPaymentConfigSchema,
    // required if enabled is true
    preorderCollectShippingAddress: z.boolean().optional(),
    preorderProdStart: z.string().optional(),
    // required if enabled is true
    orderFee: z.number().optional(),
    // required if enabled is true
    vipOrderFee: z.number().optional(),
    // required if enabled is true
    takeCutFromPreorders: z.boolean().optional(),
});
// ============================================================================
// Checkout Schema
// ============================================================================
const programOwnerCheckoutAssignmentSchema = z
    .object({
    // Controls whether to dynamically select services providers or use default
    // making this broader
    dynamic: z.boolean(),
    // Default services providers mapping when dynamic is false
    // required if dynamic is false
    servicesProviders: z.record(ServicesSchema, z.string()).optional(),
    [Services.SALE]: saleAssignmentConfigurationSchema.optional(), // if dynamic is true this is required
})
    .refine((data) => {
    if (data.dynamic) {
        return data[Services.SALE] !== undefined;
    }
    return true;
}, {
    message: `${Services.SALE} is required when dynamic is true`,
    path: [Services.SALE],
})
    .refine((data) => {
    if (!data.dynamic) {
        return data.servicesProviders != null && !!data.servicesProviders[Services.TITLING];
    }
    return true;
}, {
    message: `${Services.TITLING} provider is required in servicesProviders when using static assignment`,
    path: ['servicesProviders', Services.TITLING],
});
const programOwnerCheckoutFulfillmentSchema = z.object({
    delivery: z.object({ enabled: z.boolean() }),
    // autoAssign will select closest pickup location (buyer can not choose)
    pickup: z.object({ enabled: z.boolean(), autoAssign: z.boolean() }),
});
const programOwnerCheckoutSchema = z.object({
    // assignment config informs how services providers are assigned within the checkout flow
    assignment: programOwnerCheckoutAssignmentSchema,
    // Controls which fulfillment options surface in checkout
    fulfillment: programOwnerCheckoutFulfillmentSchema,
    // checkout will show as inactive if users address is not in supported sta
    statesSupported: statesSupportedMapSchema,
    // Controls the payment options surfaced in checkout
    paymentConfig: paymentConfigSchema,
    discountCampaigns: z.record(z.string(), discountCampaignSchema),
    disclosures: checkoutDisclosuresSchema,
});
// ============================================================================
// Processing Schema
// ============================================================================
const programOwnerProcessingSchema = z.object({
    // true if we are integrating with an inventory system for this sale workflow
    // TODO decide if this should move to the inventory provider config instead...
    useInventory: z.boolean().optional(),
    // true if we are using an early dispatch workflow for this oem
    earlyDispatch: z.boolean().optional(),
    // true if reg31 is being completed by this program owner
    completingReg31: z.boolean().optional(),
    useDistributor: z.boolean().optional(),
    distributorName: z.string().optional(),
    warehouseAddress: addressSchema.optional(),
    invoiceAddress: addressSchema.optional(),
    distributorLicenseNumber: z.string().optional(),
    defaults: z.record(z.string(), z.any()).optional(), // i.e { odometerReading: 10 }
});
// ============================================================================
// Communication Schema
// ============================================================================
const programOwnerCommunicationSchema = z.object({
    reEngagementConfig: reEngagementConfigSchema.optional(),
    excludedEmailIds: z.array(z.string()).optional(),
});
// ============================================================================
// Core Schema
// ============================================================================
const programOwnerServiceConfigSchema = serviceConfigSchema.extend({
    website: programOwnerWebsiteSchema,
    modal: z.object({}).passthrough(), // no config here yet
    configurator: programOwnerConfiguratorSchema,
    preorder: programOwnerPreorderSchema,
    checkout: programOwnerCheckoutSchema,
    processing: programOwnerProcessingSchema,
    communication: programOwnerCommunicationSchema,
    product: productConfigurationSchema.optional(),
    numVehiclesTowardTiers: z.number().optional(),
    cosmeticReportEnabled: z.boolean().optional(),
});
// ============================================================================
// DTO Schema
// ============================================================================
// ----------------------------------------
// Checkout DTOs
// ----------------------------------------
// Assignment
//
const programOwnerCheckoutAssignmentDtoSchema = z
    .object({
    dynamic: z.boolean(),
    servicesProviders: z.record(ServicesSchema, z.string()).optional(),
    [Services.SALE]: saleAssignmentConfigurationSchema.optional(),
})
    .refine((data) => {
    if (data.dynamic) {
        return data[Services.SALE] !== undefined;
    }
    return true;
}, {
    message: `Assignment configuration is required.`,
    path: [Services.SALE],
})
    .refine((data) => {
    if (!data.dynamic) {
        return data.servicesProviders != null && !!data.servicesProviders[Services.TITLING];
    }
    return true;
}, {
    message: `${Services.TITLING} provider is required in servicesProviders when using static assignment`,
    path: ['servicesProviders', Services.TITLING],
});
// Fulfillment
const programOwnerCheckoutFulfillmentDtoSchema = programOwnerCheckoutFulfillmentSchema;
// Preorder
const programOwnerPreorderDtoSchema = programOwnerPreorderSchema;
// States Supported
const programOwnerCheckoutStatesSupportedDtoSchema = statesSupportedMapSchema;
// Disclosures
const programOwnerCheckoutDisclosuresDtoSchema = checkoutDisclosuresSchema;

export { SaleAssignmentMethod, SaleAssignmentMethodSchema, programOwnerCheckoutAssignmentDtoSchema, programOwnerCheckoutAssignmentSchema, programOwnerCheckoutDisclosuresDtoSchema, programOwnerCheckoutFulfillmentDtoSchema, programOwnerCheckoutFulfillmentSchema, programOwnerCheckoutSchema, programOwnerCheckoutStatesSupportedDtoSchema, programOwnerCommunicationSchema, programOwnerConfiguratorSchema, programOwnerPreorderDtoSchema, programOwnerPreorderSchema, programOwnerProcessingSchema, programOwnerServiceConfigSchema, programOwnerWebsiteSchema, saleAssignmentConfigurationLimitedStateSchema, saleAssignmentConfigurationSchema, saleAssignmentMethodValues, statesSupportedMapSchema };
//# sourceMappingURL=ProgramOwnerServiceConfig.js.map
